import { type RefObject } from 'react';

import { fromPromise } from 'xstate';

import { type VirtualizedListHandle } from '../scrollspy-virtualized.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Jumps to the requested row and keeps the list on it while the layout settles.
 *
 * Rows keep changing after a jump lands: rows load in above the target, and
 * rows around it are measured as they render. FlashList anchors the first
 * visible row rather than the target, and pauses its own correction right after
 * `scrollToIndex`, so those changes push the target out of view. The target's
 * index is looked up again on every pass, and the jump repeats whenever the
 * target moves, until it holds still or the user scrolls.
 */
const anchorRequestedTarget = fromPromise<void, AnchorRequestedTargetInput>(
  async ({ input, signal }) => {
    await keepTargetAnchored({ ...input, signal, lastJump: undefined });
  },
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function keepTargetAnchored(params: KeepTargetAnchoredParams): Promise<void> {
  const { listRef, targetId, findTargetIndex, signal, lastJump } = params;

  const list = listRef.current;
  const index = findTargetIndex(targetId);

  if (!list || index === -1 || signal.aborted) {
    return;
  }

  // NOTE: Only the user moves the list between jumps, since FlashList leaves
  //       the offset alone while it remeasures rows right after a jump. The
  //       check comes first so a scroll is never pulled back to the target.
  const scrolledDistance = lastJump
    ? Math.abs(list.getAbsoluteLastScrollOffset() - lastJump.offset)
    : 0;
  const hasUserScrolled = scrolledDistance > USER_SCROLL_TOLERANCE;

  if (hasUserScrolled) {
    return;
  }

  // NOTE: Comparing against the position the last jump was computed from also
  //       catches movement during the jump.
  const position = readTargetPosition(list, index);

  if (!lastJump || position !== lastJump.position) {
    await list.scrollToIndex({ index, animated: false });

    const offset = list.getAbsoluteLastScrollOffset();

    await keepTargetAnchored({ ...params, lastJump: { position, offset, at: Date.now() } });

    return;
  }

  const hasSettled = Date.now() - lastJump.at >= ANCHOR_SETTLE_DURATION;

  if (hasSettled) {
    return;
  }

  await wait(ANCHOR_POLL_INTERVAL);

  await keepTargetAnchored(params);
}

function readTargetPosition(list: VirtualizedListHandle, index: number) {
  return `${index}:${list.getLayout(index)?.y}`;
}

function wait(duration: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, duration);
  });
}

// ─────────────────────────────────────────────────────────────────────────────

export const Actors = {
  anchorRequestedTarget,
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ANCHOR_POLL_INTERVAL = 100;
const ANCHOR_SETTLE_DURATION = 1000;
const USER_SCROLL_TOLERANCE = 2;

// ─── Types ───────────────────────────────────────────────────────────────────

type AnchorRequestedTargetInput = {
  listRef: RefObject<VirtualizedListHandle | null>;
  targetId: string;
  findTargetIndex: (targetId: string) => number;
};

type KeepTargetAnchoredParams = AnchorRequestedTargetInput & {
  signal: AbortSignal;
  lastJump: { position: string; offset: number; at: number } | undefined;
};
